import { useState } from 'react';
import {
  Content,
  InfoCard,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { usePermission } from '@backstage/plugin-permission-react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import EditIcon from '@material-ui/icons/Edit';
import RefreshIcon from '@material-ui/icons/Refresh';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import {
  releaseConfigurePermission,
  workloadsOf,
} from '@internal/backstage-plugin-release-common';
import { ConfigMatrixTable } from './ConfigMatrixTable';
import { ConfigureDialog } from './ConfigureDialog';
import { DriftLegend } from './DriftLegend';
import { ScalingSection } from './ScalingSection';
import { Cell, Draft, cellKey, pendingChanges } from './draft';
import { useServiceValues } from './useServiceValues';
import { useSetValues } from './useSetValues';

const useStyles = makeStyles(theme => ({
  // MUI's toggle buttons shout in uppercase; these are the values keys as
  // written in the file, and reading `API` for `api` would be a lie.
  toggle: {
    textTransform: 'none',
    padding: theme.spacing(0.5, 2),
  },
  button: {
    textTransform: 'none',
  },
}));

/**
 * What one component's overlays hold, side by side across its environments,
 * and the place to change it.
 *
 * The screen exists for the miss: a key set in dev and never carried to
 * staging, a memory bump that reached one file and not the other. One
 * workload is shown at a time — api or ep, chosen at the top right — as two
 * named cards: its scaling numbers, then its environment variables, with the
 * environments as columns so a gap is a blank cell rather than a diff nobody
 * ran.
 *
 * Editing happens in the same cells. Nothing is written from here: Review
 * hands the changes to a scaffolder task, which opens one pull request per
 * environment, and the permission that matters is checked there.
 */
export const EntityConfigurationPage = () => {
  const { entity } = useEntity();
  const classes = useStyles();
  const state = useServiceValues(entity.metadata.name);
  const [chosen, setChosen] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>({});
  const [newKeys, setNewKeys] = useState<Record<string, string[]>>({});
  const [reviewing, setReviewing] = useState(false);

  // Courtesy, not control — the gate is on the scaffolder action. The base
  // permission decides whether to offer editing at all; production's is the
  // dialog's to check, per environment.
  const canConfigure = usePermission({ permission: releaseConfigurePermission }).allowed;

  const stopEditing = () => {
    setEditing(false);
    setReviewing(false);
    setDraft({});
    setNewKeys({});
  };
  const start = useSetValues(stopEditing);

  if (state.kind === 'loading') {
    return (
      <Content>
        <Progress />
      </Content>
    );
  }

  if (state.kind === 'error') {
    return (
      <Content>
        <ResponseErrorPanel error={state.error} />
      </Content>
    );
  }

  const values = state.response;
  const workloads = workloadsOf(values);
  // Keep the choice honest when the data changes under it.
  const workload = chosen && workloads.includes(chosen) ? chosen : workloads[0];
  const changes = pendingChanges(values, draft, newKeys);

  const onDraft = (cell: Cell, value: string) =>
    setDraft(current => ({ ...current, [cellKey(cell)]: value }));
  const onAddKey = (key: string) =>
    setNewKeys(current => ({
      ...current,
      [workload]: [...(current[workload] ?? []), key],
    }));

  const editor = editing
    ? { draft, onDraft, onAddKey, newKeys: newKeys[workload] ?? [] }
    : undefined;

  return (
    <Content>
      <Box pb={2}>
        <Box display="flex" alignItems="center" gridGap={16} flexWrap="wrap">
          <Box display="flex" alignItems="center" gridGap={4} flexGrow={1}>
            <Typography variant="h5">Service {values.service}</Typography>
            <Tooltip
              title={
                state.cooling
                  ? 'Just read — try again in a moment'
                  : 'Read again, past the cache'
              }
            >
              <span>
                <IconButton
                  size="small"
                  aria-label="Refresh configuration"
                  disabled={state.cooling || editing}
                  onClick={state.refresh}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {/* Only when there is a choice: a control with one option is noise. */}
          {workloads.length > 1 && (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={workload}
              aria-label="Workload"
              onChange={(_event, next: string | null) => next && setChosen(next)}
            >
              {workloads.map(option => (
                <ToggleButton
                  key={option}
                  value={option}
                  className={classes.toggle}
                  aria-label={option}
                >
                  {option}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}

          {editing ? (
            <>
              <Button className={classes.button} onClick={stopEditing}>
                Cancel
              </Button>
              <Button
                className={classes.button}
                variant="contained"
                color="primary"
                disabled={changes.length === 0}
                onClick={() => setReviewing(true)}
              >
                Review {changes.length > 0 ? `${changes.length} ` : ''}
                {changes.length === 1 ? 'change' : 'changes'}
              </Button>
            </>
          ) : (
            <Tooltip
              title={
                canConfigure
                  ? 'Change values in the cells, then review'
                  : `You cannot change configuration. This needs ${releaseConfigurePermission.name}.`
              }
            >
              <span>
                <Button
                  className={classes.button}
                  variant="outlined"
                  color="primary"
                  startIcon={<EditIcon fontSize="small" />}
                  disabled={!canConfigure}
                  onClick={() => setEditing(true)}
                >
                  Edit
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
        {/* Said up front because it is easy to assume otherwise: dev
            ConfigMaps are also edited live in the ArgoCD UI, and a live edit
            is not in the file. This is the file. */}
        <Typography variant="body2" color="textSecondary" component="p">
          As committed in gitops-platform. A value changed live in a cluster
          without a commit is not shown here, and is overwritten on the next
          sync.
        </Typography>
      </Box>

      {workload && (
        <Box display="grid" gridGap={16}>
          <InfoCard
            title="Configuration"
            subheader="Resources"
            titleTypographyProps={{ variant: 'h6' }}
            noPadding
          >
            <ScalingSection values={values} workload={workload} editing={editor} />
          </InfoCard>

          <InfoCard
            title="Environment variables"
            subheader={<DriftLegend values={values} workload={workload} />}
            titleTypographyProps={{ variant: 'h6' }}
            noPadding
          >
            <ConfigMatrixTable values={values} workload={workload} editing={editor} />
          </InfoCard>
        </Box>
      )}

      <ConfigureDialog
        open={reviewing}
        values={values}
        changes={changes}
        onClose={() => setReviewing(false)}
        onConfirm={start}
      />
    </Content>
  );
};
